import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Banknote, Download, Calendar, Play, Filter, TrendingUp, Users, FileText, Building2, DollarSign, Search, Eye, X, Pencil, LockOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { EditPayslipDialog } from '../components/payroll/EditPayslipDialog';
import { AppraisalTab } from '../components/payroll/AppraisalTab';
import { YtdSummaryCard } from '../components/payroll/YtdSummaryCard';
import { StatutoryContributions } from '../components/payroll/StatutoryContributions';
import { RunStateButtons } from '../components/payroll/RunStateButtons';

export default function Payroll() {
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processMonth, setProcessMonth] = useState(new Date().getMonth() + 1);
  const [processYear, setProcessYear] = useState(new Date().getFullYear());
  const [processing, setProcessing] = useState(false);
  // Optional: select an employee to run payroll for just that person.
  const [processEmployeeId, setProcessEmployeeId] = useState('');
  // Employees eligible for the selected period: active, on notice, and leavers whose LWD is in/after the month.
  const scopeEmployees = useMemo(() => {
    const mm = String(processMonth).padStart(2, '0');
    const monthStart = `${processYear}-${mm}-01`;
    const monthEnd = `${processYear}-${mm}-${String(new Date(processYear, processMonth, 0).getDate()).padStart(2, '0')}`;
    return employees
      .map((e) => {
        const join = (e.join_date || e.joining_date || '').slice(0, 10);
        if (join && join > monthEnd) return null;
        const st = e.status || 'active';
        if (st === 'active' || st === 'on_notice_period') return { ...e, scopeLwd: null };
        if (st !== 'terminated') return null;
        const lwd = (e.termination_details?.last_working_day || e.resignation_date || '').slice(0, 10);
        return lwd && lwd >= monthStart ? { ...e, scopeLwd: lwd } : null;
      })
      .filter(Boolean);
  }, [employees, processMonth, processYear]);
  const scopeLeavers = scopeEmployees.filter((e) => e.scopeLwd);
  
  // Filters
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('payslips'); // payslips, contributions
  
  // Contribution Report Dialog
  const [contributionDialogOpen, setContributionDialogOpen] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const canProcess = ['admin', 'payroll_officer', 'hr_manager'].includes(user.role);
  const canViewAll = ['admin', 'payroll_officer', 'hr_manager'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (canViewAll) {
        // Admin, HR Manager, Payroll Officer can see all data
        const [runsRes, slipsRes, employeesRes] = await Promise.all([
          axios.get(`${API}/payroll/runs`),
          axios.get(`${API}/payslips`),
          axios.get(`${API}/employees`)
        ]);
        setPayrollRuns(runsRes.data);
        setPayslips(slipsRes.data);
        setEmployees(employeesRes.data);
      } else {
        // Regular employees can only see their own payslips
        const employeesRes = await axios.get(`${API}/employees`);
        const currentEmployee = employeesRes.data.find(emp => emp.email === user.email);
        
        if (currentEmployee) {
          const slipsRes = await axios.get(`${API}/me/payslips`);
          setPayslips(slipsRes.data);
          setEmployees([currentEmployee]);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch payroll data');
    } finally {
      setLoading(false);
    }
  };

  // Filter payslips by month and year
  const filteredPayslips = useMemo(() => {
    let filtered = payslips;
    
    // Filter by year
    if (filterYear !== 'all') {
      filtered = filtered.filter(slip => slip.year === parseInt(filterYear));
    }
    
    // Filter by month
    if (filterMonth !== 'all') {
      filtered = filtered.filter(slip => slip.month === parseInt(filterMonth));
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(slip => {
        const employee = employees.find(e => e.employee_id === slip.employee_id);
        return (
          employee?.full_name?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query) ||
          slip.employee_id?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [payslips, filterMonth, filterYear, searchQuery, employees]);

  // Calculate contribution totals for a given month/year
  const contributionTotals = useMemo(() => {
    const slipsToCalculate = filteredPayslips;
    
    return {
      epf_employee: slipsToCalculate.reduce((sum, s) => sum + (s.epf_employee || 0), 0),
      epf_employer: slipsToCalculate.reduce((sum, s) => sum + (s.epf_employer || 0), 0),
      socso_employee: slipsToCalculate.reduce((sum, s) => sum + (s.socso_employee || 0), 0),
      socso_employer: slipsToCalculate.reduce((sum, s) => sum + (s.socso_employer || 0), 0),
      eis_employee: slipsToCalculate.reduce((sum, s) => sum + (s.eis_employee || 0), 0),
      eis_employer: slipsToCalculate.reduce((sum, s) => sum + (s.eis_employer || 0), 0),
      pcb_tax: slipsToCalculate.reduce((sum, s) => sum + (s.pcb_tax || 0), 0),
      hrdf: slipsToCalculate.reduce((sum, s) => sum + (s.hrdf || 0), 0),  // HRDF levy
      total_gross: slipsToCalculate.reduce((sum, s) => sum + (s.gross_salary || 0), 0),
      total_net: slipsToCalculate.reduce((sum, s) => sum + (s.net_salary || 0), 0),
      count: slipsToCalculate.length
    };
  }, [filteredPayslips]);

  const handleProcessPayroll = async () => {
    setProcessing(true);
    try {
      // Payroll is now async — endpoint returns immediately with a payroll_id;
      // we poll the run-status endpoint for completion.
      const empParam = processEmployeeId ? `&employee_id=${encodeURIComponent(processEmployeeId)}` : '';
      const startRes = await axios.post(`${API}/payroll/process?month=${processMonth}&year=${processYear}${empParam}`);
      const payrollId = startRes.data?.payroll_id;
      const skippedExisting = startRes.data?.skipped_existing || [];
      toast.info(
        processEmployeeId
          ? `Payroll started for employee ${processEmployeeId} (${processMonth}/${processYear})...`
          : `Payroll started for ${startRes.data?.total_employees ?? ''} employee(s) (${processMonth}/${processYear}). Processing in background...`
      );
      if (skippedExisting.length > 0) {
        const names = skippedExisting.slice(0, 5).map((s) => `• ${s.full_name || s.employee_id} — ${s.reason}`).join('\n');
        const extra = skippedExisting.length > 5 ? `\n…and ${skippedExisting.length - 5} more.` : '';
        toast.warning(
          `${skippedExisting.length} employee(s) already had a payslip for ${processMonth}/${processYear} and were skipped (no duplicates created).\n${names}${extra}`,
          { duration: 15000 }
        );
      }

      // Poll up to 3 minutes (90 attempts × 2s)
      let done = false;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const r = await axios.get(`${API}/payroll/runs/${payrollId}`);
          if (r.data?.status === 'completed') {
            done = true;
            const failedCount = r.data.failed_count || 0;
            const failed = r.data.failed_employees || [];
            if (failedCount > 0) {
              // Iter125 — show a sustained warning toast listing skipped
              // employees so HR fixes their config before re-running.
              const lines = failed.slice(0, 5).map((f) =>
                `• ${f.employee_name || f.employee_id}: ${f.reason}`
              ).join('\n');
              const extra = failed.length > 5 ? `\n…and ${failed.length - 5} more.` : '';
              toast.warning(
                `Payroll completed with ${failedCount} skipped employee(s) — deductions exceeded gross salary.\n${lines}${extra}`,
                { duration: 20000 }
              );
            } else {
              const skippedNote = skippedExisting.length ? ` (${skippedExisting.length} skipped — payslip already existed)` : '';
              toast.success(`Payroll completed! ${r.data.total_employees} employee(s) processed${skippedNote}, Gross RM ${(r.data.total_gross || 0).toLocaleString()}`);
            }
            break;
          }
          if (r.data?.status === 'failed') {
            toast.error('Payroll processing failed. Check logs.');
            break;
          }
        } catch (_) { /* keep polling */ }
      }
      if (!done) {
        toast.warning('Payroll is still processing. Refresh the page in a minute to see results.');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payroll');
    } finally {
      setProcessing(false);
    }
  };

  // View payslip details
  const viewPayslipDetails = (slip) => {
    const employee = employees.find(e => e.employee_id === slip.employee_id);
    setSelectedPayslip({ ...slip, employee });
    setContributionDialogOpen(true);
    // Iter127 — Fetch the audit trail of manual adjustments so HR/payroll
    // can see what was added/deducted and WHY. Reported by Ankur
    // 18-Jun-2026: adjustment remarks/reasons weren't displayed anywhere.
    setAdjustments([]);
    axios
      .get(`${API}/payslips/${slip.payslip_id}/adjustments`)
      .then((r) => setAdjustments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAdjustments([]));
  };

  // --- Send Payslip Email ---
  const [sendingEmailId, setSendingEmailId] = useState(null);
  // --- Payslip Adjustment dialog ---
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: '', type: 'bonus', reason: '' });
  const [adjSaving, setAdjSaving] = useState(false);
  // Iter127 — Manual adjustment audit trail shown in payslip modal.
  const [adjustments, setAdjustments] = useState([]);
  // --- Comparison panel ---
  const [comparison, setComparison] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  // --- Full payslip edit (HR feedback: editable payroll + global control) ---
  const [editOpen, setEditOpen] = useState(false);

  const refreshAdjustments = async (payslipId) => {
    try {
      const r = await axios.get(`${API}/payslips/${payslipId}/adjustments`);
      setAdjustments(Array.isArray(r.data) ? r.data : []);
    } catch (_) {/* non-fatal */}
  };

  const handleEditSaved = (result) => {
    if (result?.payslip) setSelectedPayslip((prev) => ({ ...prev, ...result.payslip }));
    if (selectedPayslip?.payslip_id) refreshAdjustments(selectedPayslip.payslip_id);
    fetchData();
  };

  const handleUnlockPayslip = async () => {
    if (!selectedPayslip?.payslip_id) return;
    const reason = window.prompt('Reason for unlocking this finalized payslip (required, min 5 characters):');
    if (reason === null) return;
    if (reason.trim().length < 5) { toast.error('Please provide a reason of at least 5 characters'); return; }
    try {
      const res = await axios.post(`${API}/payslips/${selectedPayslip.payslip_id}/unlock`, { reason: reason.trim() });
      toast.success('Payslip unlocked — it is now a draft and can be edited');
      setSelectedPayslip((prev) => ({ ...prev, ...res.data.payslip }));
      refreshAdjustments(selectedPayslip.payslip_id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock payslip');
    }
  };

  const submitAdjustment = async () => {
    if (!selectedPayslip?.payslip_id) return;
    if (!adjForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!parseFloat(adjForm.amount) || parseFloat(adjForm.amount) === 0) {
      toast.error('Amount must be non-zero');
      return;
    }
    setAdjSaving(true);
    try {
      const res = await axios.post(`${API}/payslips/${selectedPayslip.payslip_id}/adjustments`, {
        amount: parseFloat(adjForm.amount),
        type: adjForm.type,
        reason: adjForm.reason.trim(),
      });
      toast.success('Adjustment applied');
      // Re-fetch the payslip details to refresh the dialog
      setSelectedPayslip({ ...selectedPayslip, ...res.data.payslip });
      // Iter127 — refresh the audit trail so the new row appears immediately.
      try {
        const r = await axios.get(`${API}/payslips/${selectedPayslip.payslip_id}/adjustments`);
        setAdjustments(Array.isArray(r.data) ? r.data : []);
      } catch (_) {/* non-fatal */}
      setAdjOpen(false);
      setAdjForm({ amount: '', type: 'bonus', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply adjustment');
    } finally {
      setAdjSaving(false);
    }
  };

  const loadComparison = async () => {
    if (!selectedPayslip?.employee_id) return;
    setCompLoading(true);
    setComparison(null);
    try {
      const res = await axios.get(`${API}/payroll/comparison?employee_id=${selectedPayslip.employee_id}&month=${selectedPayslip.month}&year=${selectedPayslip.year}`);
      setComparison(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load comparison');
    } finally {
      setCompLoading(false);
    }
  };
  const handleSendPayslipEmail = async () => {
    if (!selectedPayslip?.payslip_id) return;
    setSendingEmailId(selectedPayslip.payslip_id);
    try {
      const res = await axios.post(`${API}/payslips/${selectedPayslip.payslip_id}/send-email`);
      if (res.data?.status === 'sent') {
        toast.success(res.data.message || 'Payslip email sent');
      } else if (res.data?.status === 'disabled') {
        toast.info(res.data.message || 'Email service is not configured (MOCKED).');
      } else {
        toast.message('Email queued.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payslip email');
    } finally {
      setSendingEmailId(null);
    }
  };

  // --- Finalize Single Payslip (Iter56 — Ankur 25-Jun-2026) ---
  // Previously this button was "Finalize Run" which locked every payslip
  // in the same monthly payroll_run (all employees that month). Testers
  // expected per-employee semantics because the button lived inside the
  // per-employee payslip dialog. Now the button finalizes ONLY the
  // currently-viewed payslip; the bulk action moved to a separate "Finalize
  // Entire Month" button next to it.
  const handleFinalizePayslip = async () => {
    if (!selectedPayslip?.payslip_id) return;
    if (!window.confirm(
      `Finalize ONLY this payslip for ${selectedPayslip.full_name || selectedPayslip.employee_name || 'this employee'} `
      + `(${selectedPayslip.month}/${selectedPayslip.year})? It will be locked from edits. Other employees' payslips this month are not affected.`
    )) return;
    try {
      await axios.put(`${API}/payslips/${selectedPayslip.payslip_id}/finalize`);
      toast.success('Payslip finalized');
      setContributionDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    }
  };

  const handleFinalizeRun = async () => {
    if (!selectedPayslip?.payroll_id) return;
    if (!window.confirm(
      `Finalize the ENTIRE payroll run for ${selectedPayslip.month}/${selectedPayslip.year}? `
      + `Every employee's payslip in this month will be locked from edits.`
    )) return;
    try {
      const res = await axios.put(`${API}/payroll/runs/${selectedPayslip.payroll_id}/finalize`);
      toast.success(`Finalized ${res.data?.finalized_payslips ?? 0} payslip(s)`);
      setContributionDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    }
  };

  // --- Payroll Summary Report ---
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const fetchSummaryReport = async () => {
    setReportLoading(true);
    setReportData(null);
    try {
      const res = await axios.get(`${API}/payroll/summary-report?month=${reportMonth}&year=${reportYear}`);
      setReportData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate summary report');
    } finally {
      setReportLoading(false);
    }
  };

  const exportSummaryReportToExcel = async () => {
    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      toast.error('No payroll data to export. Generate the report first.');
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const monthName = new Date(reportYear, reportMonth - 1, 1).toLocaleDateString('en-US', { month: 'long' });

      // 1) Employee rows — exact same fields shown in the modal table
      const rows = reportData.rows.map((r) => ({
        'Employee ID': r.employee_id,
        'Employee Name': r.employee_name || '',
        'Pay Period': r.final_month
          ? `Final month (LWD ${r.last_working_day || ''}${r.eligible_days != null ? `, ${r.eligible_days}/${r.days_in_month} days` : ''})`
          : r.prorated ? `Prorated (${r.proration_reason || ''})` : 'Full month',
        'Basic (RM)': Number(r.basic_salary || 0).toFixed(2),
        'Allowances (RM)': Number(r.allowances || 0).toFixed(2),
        'Overtime (RM)': Number(r.overtime || 0).toFixed(2),
        'Leave Encashment (RM)': Number(r.leave_encashment || 0).toFixed(2),
        'Gross (RM)': Number(r.gross_salary || 0).toFixed(2),
        'Deductions (RM)': Number(r.total_deductions || 0).toFixed(2),
        'Net (RM)': Number(r.net_salary || 0).toFixed(2),
      }));

      // 2) Append a TOTALS row using values from the modal cards (no recalculation)
      rows.push({
        'Employee ID': '',
        'Employee Name': 'TOTAL',
        'Pay Period': '',
        'Basic (RM)': '',
        'Allowances (RM)': '',
        'Overtime (RM)': '',
        'Leave Encashment (RM)': Number(reportData.totals?.total_leave_encashment || 0).toFixed(2),
        'Gross (RM)': Number(reportData.totals?.total_gross || 0).toFixed(2),
        'Deductions (RM)': Number(reportData.totals?.total_deductions || 0).toFixed(2),
        'Net (RM)': Number(reportData.totals?.total_net || 0).toFixed(2),
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 14 }, { wch: 26 }, { wch: 34 }, { wch: 12 }, { wch: 14 },
        { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 12 },
      ];

      // Separate sheet with the summary header (same numbers as the modal cards)
      const summary = [
        ['Payroll Summary Report'],
        [`Period`, `${monthName} ${reportYear}`],
        [],
        ['Total Employees', reportData.totals?.total_employees ?? 0],
        ['Total Gross (RM)', Number(reportData.totals?.total_gross || 0).toFixed(2)],
        ['Total Deductions (RM)', Number(reportData.totals?.total_deductions || 0).toFixed(2)],
        ['Total Net (RM)', Number(reportData.totals?.total_net || 0).toFixed(2)],
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet(summary);
      summaryWs['!cols'] = [{ wch: 28 }, { wch: 22 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      XLSX.utils.book_append_sheet(wb, ws, 'Employees');

      const fname = `payroll-summary-${monthName}-${reportYear}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${fname}`);
    } catch (err) {
      toast.error('Excel export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {canViewAll ? 'Payroll Management' : 'My Payslips'}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {canViewAll 
              ? `${payrollRuns.length} payroll runs • ${payslips.length} payslips` 
              : `${payslips.length} payslips available`
            }
          </p>
        </div>
        {canProcess && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              data-testid="open-payroll-summary-report"
              onClick={() => { setReportOpen(true); setReportData(null); }}
              className="border-violet-400 text-violet-700 hover:bg-violet-50 dark:border-violet-500 dark:text-violet-200 dark:bg-violet-900/20 dark:hover:bg-violet-900/40"
            >
              <FileText className="h-4 w-4 mr-2" />
              Summary Report
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700" data-testid="process-payroll-btn">
                <Play className="h-4 w-4 mr-2" />
                Process Payroll
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-slate-800">
              <DialogHeader>
                <DialogTitle className="dark:text-slate-100">Process Monthly Payroll</DialogTitle>
              </DialogHeader>
              <div className="space-y-4" data-testid="process-payroll-form">
                <div>
                  <Label className="dark:text-slate-100">Month</Label>
                  <select
                    value={processMonth}
                    onChange={(e) => setProcessMonth(parseInt(e.target.value))}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleDateString('en-MY', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="dark:text-slate-100">Year</Label>
                  <select
                    value={processYear}
                    onChange={(e) => setProcessYear(parseInt(e.target.value))}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="dark:text-slate-100">Scope</Label>
                  <select
                    data-testid="payroll-scope-select"
                    value={processEmployeeId}
                    onChange={(e) => setProcessEmployeeId(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="">
                      All eligible employees ({scopeEmployees.length}{scopeLeavers.length ? `, incl. ${scopeLeavers.length} leaver${scopeLeavers.length > 1 ? 's' : ''}` : ''})
                    </option>
                    {scopeEmployees.map((e) => (
                      <option key={e.employee_id} value={e.employee_id} data-testid={`payroll-scope-option-${e.employee_id}`}>
                        {e.full_name} ({e.employee_id}){e.scopeLwd ? ` — leaver · LWD ${e.scopeLwd}` : e.status === 'on_notice_period' ? ' — on notice' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Lists active staff, staff on notice, and leavers whose last working day falls in {new Date(processYear, processMonth - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })} (paid prorated to their LWD).
                  </p>
                  {scopeLeavers.length > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1" data-testid="payroll-scope-leavers-note">
                      Final-month payroll this period: {scopeLeavers.map((e) => `${e.full_name} (LWD ${e.scopeLwd})`).join(', ')}
                    </p>
                  )}
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Note:</strong> {processEmployeeId
                      ? 'Payroll will be generated only for the selected employee. Duplicate payslips for the same employee+month are blocked.'
                      : 'This will process payroll for all eligible employees (leavers prorated to their last working day) and generate payslips with Malaysia statutory deductions (EPF, SOCSO, EIS, PCB).'}
                  </p>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessPayroll}
                    disabled={processing}
                    className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                  >
                    {processing ? 'Processing...' : 'Process Payroll'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Tabs for HR Admin */}
      {canViewAll && (
        <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('payslips')}
            data-testid="payroll-tab-payslips"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'payslips'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Payslips
          </button>
          <button
            onClick={() => setActiveTab('contributions')}
            data-testid="payroll-tab-contribution"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'contributions'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Contribution Reports
          </button>
          <button
            onClick={() => setActiveTab('appraisal')}
            data-testid="payroll-tab-appraisal"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'appraisal'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <TrendingUp className="h-4 w-4 inline mr-2" />
            Appraisal
          </button>
        </div>
      )}

      {/* Appraisal / Salary revisions Tab */}
      {canViewAll && activeTab === 'appraisal' && (
        <AppraisalTab employees={employees} canProcess={canProcess} />
      )}

      {/* Filters */}
      {activeTab !== 'appraisal' && (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid="payroll-filters">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filter:</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Year:</Label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="h-9 rounded-lg border border-slate-300 dark:border-slate-600 px-3 text-sm dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="all">All Years</option>
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Month:</Label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="h-9 rounded-lg border border-slate-300 dark:border-slate-600 px-3 text-sm dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="all">All Months</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleDateString('en-MY', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          
          {canViewAll && (
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search employee..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          )}
          
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Showing {filteredPayslips.length} of {payslips.length} payslips
          </div>
        </div>
      </div>
      )}

      {/* Contribution Reports Tab */}
      {canViewAll && activeTab === 'contributions' && (
        <div className="space-y-6">
          {/* Contribution Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">EPF (KWSP)</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                RM {(contributionTotals.epf_employee + contributionTotals.epf_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Employee: RM {contributionTotals.epf_employee.toFixed(2)} • Employer: RM {contributionTotals.epf_employer.toFixed(2)}
              </p>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">SOCSO (PERKESO)</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                RM {(contributionTotals.socso_employee + contributionTotals.socso_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Employee: RM {contributionTotals.socso_employee.toFixed(2)} • Employer: RM {contributionTotals.socso_employer.toFixed(2)}
              </p>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">EIS (SIP)</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                RM {(contributionTotals.eis_employee + contributionTotals.eis_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Employee: RM {contributionTotals.eis_employee.toFixed(2)} • Employer: RM {contributionTotals.eis_employer.toFixed(2)}
              </p>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">PCB (Tax)</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                RM {contributionTotals.pcb_tax.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {contributionTotals.count} employees
              </p>
            </div>
            
            {/* HRDF Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">HRDF (Training)</span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                RM {contributionTotals.hrdf.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Employer only (1%)
              </p>
            </div>
          </div>

          {/* Contribution Summary Table */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Contribution Summary
                {filterMonth !== 'all' && filterYear !== 'all' && (
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    for {new Date(parseInt(filterYear), parseInt(filterMonth) - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                  </span>
                )}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Contribution Type</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee Share</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employer Share</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700">
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 font-medium">EPF (KWSP)</td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.epf_employee.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.epf_employer.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {(contributionTotals.epf_employee + contributionTotals.epf_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700">
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 font-medium">SOCSO (PERKESO)</td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.socso_employee.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.socso_employer.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {(contributionTotals.socso_employee + contributionTotals.socso_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700">
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 font-medium">EIS (SIP)</td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.eis_employee.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.eis_employer.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {(contributionTotals.eis_employee + contributionTotals.eis_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700">
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 font-medium">PCB (Tax)</td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.pcb_tax.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500 text-right">-</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {contributionTotals.pcb_tax.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="bg-slate-50 dark:bg-slate-900 font-bold">
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">GRAND TOTAL</td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {(contributionTotals.epf_employee + contributionTotals.socso_employee + contributionTotals.eis_employee + contributionTotals.pcb_tax).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                      RM {(contributionTotals.epf_employer + contributionTotals.socso_employer + contributionTotals.eis_employer).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-emerald-600 dark:text-emerald-400 text-right tabular-nums">
                      RM {(contributionTotals.epf_employee + contributionTotals.epf_employer + contributionTotals.socso_employee + contributionTotals.socso_employer + contributionTotals.eis_employee + contributionTotals.eis_employer + contributionTotals.pcb_tax).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-employee statutory listing + KWSP / PERKESO / LHDN upload files */}
          <StatutoryContributions payslips={payslips} filterMonth={filterMonth} filterYear={filterYear} />
        </div>
      )}

      {/* Payroll Runs */}
      {canProcess && payrollRuns.length > 0 && activeTab === 'payslips' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Payroll Runs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {payrollRuns.map((run) => (
              <div
                key={run.payroll_id}
                data-testid={`payroll-run-${run.payroll_id}`}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Banknote className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                        {new Date(run.year, run.month - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{run.payroll_id.slice(0, 8)}</p>
                    </div>
                  </div>
                  <span className={`status-badge ${run.finalized ? 'status-approved' : 'status-pending'}`} data-testid={`run-status-${run.payroll_id}`}>
                    {run.finalized ? 'finalized' : run.status}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Employees</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{run.total_employees}</span>
                  </div>
                  {run.skipped_existing_count > 0 && (
                    <div className="flex justify-between text-xs" data-testid={`run-skipped-existing-${run.payroll_id}`} title={(run.skipped_existing || []).map((s) => `${s.full_name || s.employee_id} — ${s.reason}`).join('\n')}>
                      <span className="text-amber-700 dark:text-amber-400">Skipped (payslip already existed)</span>
                      <span className="font-semibold text-amber-700 dark:text-amber-400">{run.skipped_existing_count}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Gross Total</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                      RM {run.total_gross.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Deductions</span>
                    <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
                      RM {run.total_deductions.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="border-t border-slate-100 dark:border-slate-700 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Net Total</span>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                        RM {run.total_net.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {/* Delete button — only for non-finalized runs */}
                  {!run.finalized && (
                    <button
                      type="button"
                      data-testid={`delete-run-${run.payroll_id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete the draft payroll run for ${new Date(run.year, run.month - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}? This removes ${run.total_employees} payslip(s) and allows you to re-run.`)) return;
                        try {
                          await axios.delete(`${API}/payroll/runs/${run.payroll_id}`);
                          toast.success('Draft payroll run deleted');
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to delete payroll run');
                        }
                      }}
                      className="mt-3 w-full text-xs font-medium px-3 py-1.5 rounded-md border border-rose-400 text-rose-700 hover:bg-rose-50 dark:border-rose-500 dark:text-rose-200 dark:bg-rose-900/20 dark:hover:bg-rose-900/40"
                    >
                      Delete Draft Run
                    </button>
                  )}
                  <RunStateButtons run={run} onChanged={fetchData} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payslips */}
      {activeTab === 'payslips' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {canViewAll ? 'Payslips' : 'Your Payslips'}
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Period</th>
                    {canViewAll && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Employee</th>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Gross Salary</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">EPF</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">SOCSO</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">PCB</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Net Salary</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredPayslips.map((slip) => {
                    const employee = employees.find(e => e.employee_id === slip.employee_id);
                    return (
                    <tr key={slip.payslip_id} data-testid={`payslip-${slip.payslip_id}`} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                        {new Date(slip.year, slip.month - 1, 1).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' })}
                      </td>
                      {canViewAll && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div>
                            <p className="text-slate-900 dark:text-slate-100 font-medium">{employee?.full_name || slip.employee_name || 'N/A'}</p>
                            <p className="text-slate-500 dark:text-slate-400 text-xs font-mono">{slip.employee_id}</p>
                            {(slip.final_month || /last working day/i.test(slip.proration_reason || '')) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`payslip-final-month-${slip.payslip_id}`}>
                                Final month{slip.last_working_day ? ` · LWD ${slip.last_working_day}` : ''}{slip.eligible_days != null ? ` · ${slip.eligible_days}/${slip.days_in_month} days` : ''}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100 text-right tabular-nums">
                        RM {slip.gross_salary.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                        {slip.epf_employee.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                        {slip.socso_employee.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                        {slip.pcb_tax.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600 dark:text-emerald-400 text-right tabular-nums">
                        RM {slip.net_salary.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {(() => {
                          // Single source of truth: prefer the new `status`
                          // ('draft'|'finalized'|'paid'), fall back to legacy
                          // `payment_status` ('pending'|'paid').
                          const s = (slip.status || slip.payment_status || 'pending').toLowerCase();
                          const tone = s === 'finalized' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : s === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : s === 'draft' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
                          const label = s === 'finalized' ? 'Finalized'
                            : s === 'paid' ? 'Paid'
                            : s === 'draft' ? 'Draft'
                            : 'Pending';
                          return (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tone}`} data-testid={`payslip-status-${slip.payslip_id}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => viewPayslipDetails(slip)}
                            data-testid={`view-payslip-${slip.payslip_id}`}
                            className="h-8 border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700/40"
                          >
                            <Eye className="h-4 w-4 mr-1" />View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`download-payslip-${slip.payslip_id}`}
                            className="h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('hrms_token');
                                const res = await fetch(`${API}/hr/payslips/${slip.payslip_id}/download`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (!res.ok) {
                                  const err = await res.json().catch(() => ({}));
                                  toast.error(err.detail || 'Failed to download payslip');
                                  return;
                                }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `payslip_${slip.employee_id}_${slip.month}_${slip.year}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                setTimeout(() => URL.revokeObjectURL(url), 30_000);
                                toast.success('Payslip downloaded');
                              } catch (e) {
                                toast.error('Download failed');
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {filteredPayslips.length === 0 && activeTab === 'payslips' && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <Banknote className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            {searchQuery || filterMonth !== 'all' || filterYear !== 'all' 
              ? 'No payslips found matching your filters' 
              : 'No payslips generated yet'}
          </p>
        </div>
      )}

      {/* Payslip Detail Dialog */}
      <Dialog open={contributionDialogOpen} onOpenChange={setContributionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-500" />
              Payslip Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayslip && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Employee</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium" data-testid="ps-employee-name">
                    {selectedPayslip.employee?.full_name || selectedPayslip.employee_name || 'N/A'}
                  </p>
                  <p className="text-xs text-slate-500">{selectedPayslip.employee_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Period</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {new Date(selectedPayslip.year, selectedPayslip.month - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              
              {/* Salary Breakdown */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Salary Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Basic Salary</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100" data-testid="ps-basic">RM {Number(selectedPayslip.basic_salary || 0).toFixed(2)}</span>
                  </div>
                  {Number(selectedPayslip.allowances || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Allowances</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100" data-testid="ps-allowances">+ RM {Number(selectedPayslip.allowances || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(selectedPayslip.overtime || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Overtime</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100" data-testid="ps-overtime">+ RM {Number(selectedPayslip.overtime || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(selectedPayslip.bonus || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Bonus / Incentive</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100" data-testid="ps-bonus">+ RM {Number(selectedPayslip.bonus || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(selectedPayslip.leave_encashment || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">
                        Annual Leave Encashment ({Number(selectedPayslip.leave_encashment_days || 0)} day{Number(selectedPayslip.leave_encashment_days || 0) === 1 ? '' : 's'} × basic ÷ 26)
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100" data-testid="ps-leave-encashment">+ RM {Number(selectedPayslip.leave_encashment || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {/* Iter95 (Ankur 15-Jul-2026) — Show approved Unpaid Leave
                      as a negative earnings line so the tester can visibly
                      confirm that unpaid days reduced the Gross Salary.
                      Previously this appeared under Deductions and Gross
                      looked inflated. */}
                  {Number(selectedPayslip.unpaid_leave_deduction || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">
                        Less: Unpaid Leave ({Number(selectedPayslip.unpaid_leave_days || 0)} day{Number(selectedPayslip.unpaid_leave_days || 0) === 1 ? '' : 's'})
                      </span>
                      <span className="font-medium text-red-700 dark:text-red-400" data-testid="ps-unpaid-leave">
                        - RM {Number(selectedPayslip.unpaid_leave_deduction || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2 flex justify-between">
                    <span className="font-semibold text-slate-700 dark:text-slate-200">Gross Salary</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100" data-testid="ps-gross">RM {Number(selectedPayslip.gross_salary || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Deductions */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 dark:text-red-300 mb-3">Deductions</h4>
                <div className="space-y-2 text-sm">
                  {[
                    { k: 'epf_employee', label: 'EPF (Employee 11%)' },
                    { k: 'socso_employee', label: 'SOCSO (Employee)' },
                    { k: 'eis_employee', label: 'EIS (Employee)' },
                    { k: 'pcb_tax', label: 'PCB (Tax)' },
                  ].map(({ k, label }) => {
                    const ov = selectedPayslip.statutory_overrides?.[k];
                    return (
                      <div key={k} className="flex justify-between">
                        <span className="text-red-700 dark:text-red-400 flex items-center gap-2">
                          {label}
                          {ov?.value != null && (
                            <span title={`Manual override by ${ov.by || 'HR'}: ${ov.reason || ''}`} data-testid={`ps-override-badge-${k}`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">manual</span>
                          )}
                        </span>
                        <span className="font-medium text-red-800 dark:text-red-300" data-testid={`ps-${k}`}>- RM {Number(selectedPayslip[k] || 0).toFixed(2)}</span>
                      </div>
                    );
                  })}
                  {Number(selectedPayslip.skbkk_employee || selectedPayslip.deductions_breakdown?.skbkk_employee || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-red-700 dark:text-red-400">SKBKK (staff welfare scheme)</span>
                      <span className="font-medium text-red-800 dark:text-red-300" data-testid="ps-skbkk">- RM {Number(selectedPayslip.skbkk_employee || selectedPayslip.deductions_breakdown?.skbkk_employee || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {(() => {
                    // Iter93 (Ankur 10-Jul-2026) — Prefer the
                    // `deductions_breakdown.other_deductions` value which
                    // EXCLUDES unauthorised absence (rendered as its own
                    // line below). The top-level `other_deductions` field
                    // still folds UA into it for backwards compat with
                    // total_deductions accounting, but displaying that
                    // top-level value AND the standalone UA line
                    // double-counts the same money (tester's screenshot
                    // showed Other=1750 + UA=1750 both on the same payslip).
                    const otherClean = Number(
                      selectedPayslip.deductions_breakdown?.other_deductions ??
                        selectedPayslip.other_deductions ??
                        0,
                    );
                    if (otherClean <= 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span className="text-red-700 dark:text-red-400">Other Deductions</span>
                        <span className="font-medium text-red-800 dark:text-red-300" data-testid="ps-other-ded">- RM {otherClean.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                  {/* Iter86 — Surface auto-computed Unauthorised Absence
                      deduction so HR sees exactly which working days had
                      no clock-in / leave / holiday coverage. */}
                  {Number(selectedPayslip.deductions_breakdown?.unauthorised_absence_deduction || 0) > 0 && (
                    <div>
                      <div className="flex justify-between">
                        <span className="text-red-700 dark:text-red-400">
                          Unauthorised Absence ({selectedPayslip.deductions_breakdown.unauthorised_absence_days} day{selectedPayslip.deductions_breakdown.unauthorised_absence_days === 1 ? '' : 's'})
                        </span>
                        <span className="font-medium text-red-800 dark:text-red-300" data-testid="ps-unauth-absence">
                          - RM {Number(selectedPayslip.deductions_breakdown.unauthorised_absence_deduction).toFixed(2)}
                        </span>
                      </div>
                      {selectedPayslip.deductions_breakdown.unauthorised_absence_note && (
                        <p className="text-xs text-red-600 dark:text-red-500 mt-1 italic">
                          {selectedPayslip.deductions_breakdown.unauthorised_absence_note}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="border-t border-red-200 dark:border-red-800 pt-2 mt-2 flex justify-between">
                    <span className="font-semibold text-red-700 dark:text-red-300">Total Deductions</span>
                    <span className="font-semibold text-red-800 dark:text-red-300">- RM {Number(selectedPayslip.total_deductions || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Employer Contributions */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Employer Contributions</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    {/* Iter123 — Malaysia EPF employer rate is 13% only for
                        wages ≤ RM 5,000. For wages > RM 5,000 the rate
                        drops to 12%. Show the actual applied rate so HR
                        doesn't get confused (Ankur 18-Jun-2026). */}
                    <span className="text-blue-700 dark:text-blue-400">
                      EPF (Employer {Number(selectedPayslip.gross_salary || 0) > 5000 ? '12%' : '13%'})
                    </span>
                    <span className="font-medium text-blue-800 dark:text-blue-300">RM {selectedPayslip.epf_employer?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700 dark:text-blue-400">SOCSO (Employer)</span>
                    <span className="font-medium text-blue-800 dark:text-blue-300">RM {selectedPayslip.socso_employer?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700 dark:text-blue-400">EIS (Employer)</span>
                    <span className="font-medium text-blue-800 dark:text-blue-300">RM {selectedPayslip.eis_employer?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Net Salary */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                {Number(selectedPayslip.claims_reimbursement || 0) > 0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-emerald-800 dark:text-emerald-300">Claims Reimbursement (non-taxable)</span>
                    <span className="font-medium text-emerald-800 dark:text-emerald-300" data-testid="ps-claims">+ RM {Number(selectedPayslip.claims_reimbursement || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">Net Salary</span>
                  <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="ps-net">RM {selectedPayslip.net_salary?.toFixed(2)}</span>
                </div>
              </div>

              <YtdSummaryCard payslipId={selectedPayslip.payslip_id} />

              {/* Iter127 — Manual Adjustments audit trail */}
              {adjustments.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg p-4" data-testid="adjustments-audit">
                  <h4 className="font-semibold text-amber-900 dark:text-amber-200 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Manual Adjustments ({adjustments.length})
                  </h4>
                  <div className="space-y-2">
                    {adjustments.map((a) => {
                      const t = (a.type || '').toLowerCase();
                      const positive = ['bonus', 'incentive', 'commission'].includes(t);
                      const isEdit = t === 'edit' || t === 'unlock';
                      const amt = Number(a.amount || 0);
                      return (
                        <div
                          key={a.adjustment_id}
                          className="bg-white dark:bg-slate-800 rounded-md p-3 border border-amber-200 dark:border-amber-800/40"
                          data-testid={`adjustment-row-${a.adjustment_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 capitalize">
                              {t === 'edit' ? 'Payslip edit' : t === 'unlock' ? 'Unlocked for editing' : (a.type || 'adjustment').replace('_', ' ')}
                            </span>
                            {isEdit ? (
                              <span className={`text-sm font-semibold ${amt > 0 ? 'text-emerald-700 dark:text-emerald-400' : amt < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-slate-500'}`}>
                                {amt === 0 ? '' : `Net ${amt > 0 ? '+' : '-'} RM ${Math.abs(amt).toFixed(2)}`}
                              </span>
                            ) : (
                              <span
                                className={`text-sm font-semibold ${positive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}
                              >
                                {positive ? '+' : '-'} RM {amt.toFixed(2)}
                              </span>
                            )}
                          </div>
                          {Array.isArray(a.changes) && a.changes.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {a.changes.map((c, i) => (
                                <li key={i} className="text-xs text-slate-600 dark:text-slate-400">
                                  <span className="font-medium">{c.label}</span>: {c.from == null ? 'auto' : `RM ${Number(c.from).toFixed(2)}`} → {c.to == null ? 'auto' : `RM ${Number(c.to).toFixed(2)}`}{c.note ? ` (${c.note})` : ''}
                                </li>
                              ))}
                              {a.profile_updated?.length > 0 && (
                                <li className="text-xs text-emerald-700 dark:text-emerald-400">Employee profile updated: {a.profile_updated.join(', ')}</li>
                              )}
                            </ul>
                          )}
                          {!isEdit && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 italic">
                            &ldquo;{a.reason || '(no reason provided)'}&rdquo;
                          </p>
                          )}
                          {isEdit && (t === 'unlock' ? a.reason : a.note) && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 italic">&ldquo;{t === 'unlock' ? a.reason : a.note}&rdquo;</p>
                          )}
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                            By {a.created_by || '—'} ·{' '}
                            {a.created_at ? new Date(a.created_at).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '—'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Proration notice */}
              {selectedPayslip.prorated && (
                <div className={`${selectedPayslip.final_month || /last working day/i.test(selectedPayslip.proration_reason || '') ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800'} border rounded-lg p-3 text-sm`} data-testid="proration-notice">
                  <strong className="text-violet-700 dark:text-violet-300">
                    {selectedPayslip.final_month || /last working day/i.test(selectedPayslip.proration_reason || '') ? 'Final month — prorated to last working day:' : 'Prorated payroll:'}
                  </strong>{' '}
                  <span className="text-violet-700 dark:text-violet-300">
                    {selectedPayslip.proration_reason || 'Partial month'}
                    {selectedPayslip.eligible_days != null && (
                      <> · {selectedPayslip.eligible_days}/{selectedPayslip.days_in_month} days</>
                    )}
                    {selectedPayslip.proration_factor != null && (
                      <> · factor {Number(selectedPayslip.proration_factor).toFixed(4)}</>
                    )}
                  </span>
                </div>
              )}

              {/* Month-to-month comparison */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100">Month-to-month Comparison</h4>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={loadComparison}
                    disabled={compLoading}
                    data-testid="load-comparison-btn"
                  >
                    {compLoading ? 'Loading…' : (comparison ? 'Refresh' : 'Compare to previous month')}
                  </Button>
                </div>
                {comparison && !comparison.previous && (
                  <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="no-previous-payroll">{comparison.message || 'No previous payroll found.'}</p>
                )}
                {comparison && comparison.previous && (
                  <div className="grid grid-cols-3 gap-3 text-sm" data-testid="comparison-grid">
                    {[
                      { k: 'gross_salary', label: 'Gross' },
                      { k: 'total_deductions', label: 'Deductions', invert: true },
                      { k: 'net_salary', label: 'Net' },
                    ].map(({ k, label, invert }) => {
                      const cur = Number(comparison.current?.[k] || 0);
                      const prev = Number(comparison.previous?.[k] || 0);
                      const delta = Number(comparison.delta?.[k] || 0);
                      const isUp = delta > 0;
                      const positiveColor = invert ? (isUp ? 'text-rose-600' : 'text-emerald-700') : (isUp ? 'text-emerald-700' : 'text-rose-600');
                      return (
                        <div key={k} className="bg-white dark:bg-slate-800 rounded-md p-3 border border-slate-200 dark:border-slate-700">
                          <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
                          <p className="text-xs text-slate-500">Prev: RM {prev.toFixed(2)}</p>
                          <p className="text-base font-semibold">RM {cur.toFixed(2)}</p>
                          <p className={`text-xs font-medium ${delta === 0 ? 'text-slate-500' : positiveColor}`}>
                            {delta === 0 ? 'No change' : (isUp ? '↑' : '↓')} RM {Math.abs(delta).toFixed(2)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter className="flex flex-wrap gap-2 sticky bottom-0 bg-white dark:bg-slate-800 pt-3 -mx-6 px-6 pb-1 border-t border-slate-200 dark:border-slate-700">
                <Button
                  variant="outline"
                  onClick={() => { setContributionDialogOpen(false); setComparison(null); }}
                  className="border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  Close
                </Button>
                {canProcess && (
                  <>
                    {/* Iter86b — Recompute pulls fresh approved overtime
                        + re-runs SOCSO/EIS/PCB. Visible for BOTH draft AND
                        finalized payslips so HR can fix mis-computed
                        slips after a regulatory change (e.g. our Iter86
                        SOCSO ceiling correction). */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm('Recompute this payslip from approved overtime and the latest statutory rules?')) return;
                        try {
                          const res = await axios.post(`${API}/payslips/${selectedPayslip.payslip_id}/recompute`);
                          toast.success('Payslip recomputed');
                          // Iter130 — Preserve the `.employee` reference
                          // that was attached by viewPayslipDetails. The
                          // server returns the raw payslip document
                          // (no nested employee), so spreading
                          // selectedPayslip first keeps the modal's
                          // "Employee: <name>" header from rendering N/A.
                          // Reported by Ankur 19-Jun-2026.
                          setSelectedPayslip({ ...selectedPayslip, ...res.data.payslip });
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Recompute failed');
                        }
                      }}
                      data-testid="recompute-payslip-btn"
                      className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-900/40 dark:bg-amber-900/20"
                    >
                      Recompute
                    </Button>
                    {!selectedPayslip.finalized && (
                      <>
                        <Button
                          type="button"
                          onClick={() => setEditOpen(true)}
                          data-testid="edit-payslip-btn"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Pencil className="h-4 w-4 mr-1" /> Edit Payslip
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { setAdjForm({ amount: '', type: 'bonus', reason: '' }); setAdjOpen(true); }}
                          data-testid="add-adjustment-btn"
                          className="border-indigo-400 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-200 dark:hover:bg-indigo-900/40 dark:bg-indigo-900/20"
                        >
                          + Adjustment
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Delete this draft payslip for ${selectedPayslip.month}/${selectedPayslip.year}? This will allow you to re-run payroll for this employee.`)) return;
                            try {
                              await axios.delete(`${API}/payslips/${selectedPayslip.payslip_id}`);
                              toast.success('Draft payslip deleted');
                              setContributionDialogOpen(false);
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to delete payslip');
                            }
                          }}
                          data-testid="delete-payslip-btn"
                          className="border-rose-400 text-rose-700 hover:bg-rose-50 dark:border-rose-500 dark:text-rose-200 dark:hover:bg-rose-900/40 dark:bg-rose-900/20"
                        >
                          Delete Draft
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleFinalizePayslip}
                          data-testid="finalize-this-payslip"
                          className="border-amber-400 text-amber-800 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-900/40 dark:bg-amber-900/20"
                        >
                          Finalize This Payslip
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleFinalizeRun}
                          data-testid="finalize-payroll-run"
                          className="border-amber-600 text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-100 dark:hover:bg-amber-900/60 dark:bg-amber-900/30"
                          title="Locks every employee's payslip for this month in one go"
                        >
                          Finalize Entire Month
                        </Button>
                      </>
                    )}
                    {selectedPayslip.finalized && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleUnlockPayslip}
                        data-testid="unlock-payslip-btn"
                        className="border-amber-500 text-amber-800 hover:bg-amber-50 dark:border-amber-400 dark:text-amber-200 dark:hover:bg-amber-900/40 dark:bg-amber-900/20"
                        title="Unlock this finalized payslip to make corrections (reason required)"
                      >
                        <LockOpen className="h-4 w-4 mr-1" /> Unlock to Edit
                      </Button>
                    )}
                    <Button
                      type="button"
                      data-testid="send-payslip-email"
                      onClick={handleSendPayslipEmail}
                      disabled={sendingEmailId === selectedPayslip.payslip_id}
                      variant="outline"
                      className="border-violet-400 text-violet-700 hover:bg-violet-50 dark:border-violet-500 dark:text-violet-200 dark:hover:bg-violet-900/40 dark:bg-violet-900/20"
                    >
                      {sendingEmailId === selectedPayslip.payslip_id ? 'Sending…' : 'Send Email'}
                    </Button>
                  </>
                )}
                <Button
                  data-testid="download-payslip-pdf"
                  onClick={async () => {
                    if (!selectedPayslip) return;
                    try {
                      const token = localStorage.getItem('hrms_token');
                      const res = await fetch(`${API}/hr/payslips/${selectedPayslip.payslip_id}/download`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}));
                        toast.error(err.detail || 'Failed to download payslip');
                        return;
                      }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `payslip_${selectedPayslip.employee_id}_${selectedPayslip.month}_${selectedPayslip.year}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 30_000);
                      toast.success('Payslip downloaded');
                    } catch (e) {
                      toast.error('Download failed');
                    }
                  }}
                  className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Full Payslip Edit Dialog ---- */}
      <EditPayslipDialog open={editOpen} onOpenChange={setEditOpen} payslip={selectedPayslip} onSaved={handleEditSaved} />

      {/* ---- Payroll Summary Report Dialog ---- */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Payroll Summary Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="dark:text-slate-100">Month</Label>
                <select
                  value={reportMonth}
                  onChange={(e) => setReportMonth(parseInt(e.target.value))}
                  className="mt-1 h-10 px-3 rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  data-testid="report-month-select"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{new Date(2026, m - 1, 1).toLocaleDateString('en-US', { month: 'long' })}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="dark:text-slate-100">Year</Label>
                <Input
                  type="number"
                  value={reportYear}
                  onChange={(e) => setReportYear(parseInt(e.target.value) || reportYear)}
                  className="mt-1 w-28 dark:bg-slate-900 dark:border-slate-600"
                  data-testid="report-year-input"
                />
              </div>
              <Button
                type="button"
                onClick={fetchSummaryReport}
                disabled={reportLoading}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="generate-report-btn"
              >
                {reportLoading ? 'Generating…' : 'Generate'}
              </Button>
            </div>

            {reportData && (
              <div className="space-y-3" data-testid="payroll-summary-content">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Total Employees</p>
                    <p className="font-bold text-slate-900 dark:text-slate-100">
                      {reportData.totals?.total_employees}
                      {reportData.totals?.leavers > 0 && (
                        <span className="ml-2 text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="summary-leavers-count">
                          incl. {reportData.totals.leavers} leaver{reportData.totals.leavers > 1 ? 's' : ''} (final month)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Total Gross</p>
                    <p className="font-bold text-slate-900 dark:text-slate-100">RM {Number(reportData.totals?.total_gross || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Total Deductions</p>
                    <p className="font-bold text-red-600">RM {Number(reportData.totals?.total_deductions || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Total Net</p>
                    <p className="font-bold text-emerald-700">RM {Number(reportData.totals?.total_net || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className="max-h-72 overflow-auto border border-slate-200 dark:border-slate-700 rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-2">Employee</th>
                        <th className="text-right px-2 py-2">Basic</th>
                        <th className="text-right px-2 py-2">Allow.</th>
                        <th className="text-right px-2 py-2">OT</th>
                        <th className="text-right px-2 py-2">Gross</th>
                        <th className="text-right px-2 py-2">Ded.</th>
                        <th className="text-right px-2 py-2">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {reportData.rows?.map((r) => (
                        <tr key={r.employee_id + r.basic_salary} data-testid={`summary-row-${r.employee_id}`}>
                          <td className="px-2 py-1">
                            <span className="font-mono">{r.employee_id}</span>
                            {r.employee_name && <span className="ml-1 text-slate-600 dark:text-slate-300">{r.employee_name}</span>}
                            {r.final_month && (
                              <span
                                className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                title={r.proration_reason || 'Final month — prorated to last working day'}
                                data-testid={`summary-leaver-badge-${r.employee_id}`}
                              >
                                Final month{r.last_working_day ? ` · LWD ${r.last_working_day}` : ''}{r.eligible_days != null ? ` · ${r.eligible_days}/${r.days_in_month} days` : ''}
                              </span>
                            )}
                            {!r.final_month && r.prorated && (
                              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title={r.proration_reason || 'Prorated'}>
                                Prorated{r.eligible_days != null ? ` · ${r.eligible_days}/${r.days_in_month} days` : ''}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">{Number(r.basic_salary || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right">{Number(r.allowances || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right">{Number(r.overtime || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right">{Number(r.gross_salary || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right text-red-600">{Number(r.total_deductions || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right text-emerald-700 font-semibold">{Number(r.net_salary || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportOpen(false)}
              className="border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700"
            >Close</Button>
            <Button
              type="button"
              onClick={exportSummaryReportToExcel}
              disabled={!reportData || !reportData.rows || reportData.rows.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-summary-excel-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Add Adjustment Dialog ---- */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Add Salary Adjustment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="dark:text-slate-100">Type</Label>
              <select
                value={adjForm.type}
                onChange={(e) => setAdjForm({ ...adjForm, type: e.target.value })}
                data-testid="adj-type-select"
                className="mt-1 h-10 w-full px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              >
                <option value="bonus">Bonus / Incentive (adds to gross)</option>
                <option value="commission">Commission (adds to gross)</option>
                <option value="penalty">Penalty (deduction)</option>
                <option value="deduction">Deduction (other)</option>
              </select>
            </div>
            <div>
              <Label className="dark:text-slate-100">Amount (RM)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={adjForm.amount}
                onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })}
                data-testid="adj-amount-input"
                placeholder="0.00"
                className="mt-1 dark:bg-slate-900 dark:border-slate-600"
              />
              {selectedPayslip && (adjForm.type === 'deduction' || adjForm.type === 'penalty') && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="adj-max-hint">
                  Max additional deduction: <strong>RM {Number(selectedPayslip.net_salary || 0).toFixed(2)}</strong>
                  {' '}(cannot push Net Salary below zero)
                </p>
              )}
            </div>
            <div>
              <Label className="dark:text-slate-100">Reason / Remarks <span className="text-rose-600">*</span></Label>
              <Input
                value={adjForm.reason}
                onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })}
                data-testid="adj-reason-input"
                placeholder="e.g. Performance bonus / Late submission penalty"
                className="mt-1 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAdjustment}
              disabled={adjSaving}
              data-testid="adj-save-btn"
              className="bg-violet-600 hover:bg-violet-700"
            >
              {adjSaving ? 'Saving…' : 'Apply Adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}